import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import * as i2 from '@angular/platform-browser';

const defaultNgxSeoConfig = {
    changeTitle: (title) => title,
    preserve: false,
    listenToRouteEvents: true,
};
const NGX_SEO_CONFIG_TOKEN = new InjectionToken('ngx-seo.config');

const NGX_SEO_META_KEYS = {
    keywords: 'keywords',
    description: 'description',
    ogDescription: 'og:description',
    twitterDescription: 'twitter:description',
    ogType: 'og:type',
    twitterCard: 'twitter:card',
    ogImage: 'og:image',
    twitterImage: 'twitter:image',
    image: 'image',
    ogURL: 'og:url',
    twitterURL: 'twitter:url',
    author: 'author',
    ogSiteName: 'og:site_name',
    canonical: 'canonical',
};

const NGX_SEO_TITLE_KEYS = {
    ogTitle: 'og:title',
    twitterTitle: 'twitter:title',
    name: 'name',
};

class NgxSeoService {
    constructor(config, activatedRoute, meta, router, title) {
        this.config = config;
        this.activatedRoute = activatedRoute;
        this.meta = meta;
        this.router = router;
        this.title = title;
        if (config.listenToRouteEvents) {
            this.subscribe();
        }
    }
    setSeo(seo) {
        if (!this.config.preserve) {
            this.removeMeta();
        }
        if (seo.title) {
            this.setTitle(seo.title);
        }
        if (seo.meta) {
            this.setMeta(seo.meta);
        }
    }
    setTitle(title) {
        title = this.config.changeTitle(title);
        const ogTitle = {
            property: NGX_SEO_TITLE_KEYS.ogTitle,
            content: title,
        };
        const twitterTitle = {
            name: NGX_SEO_TITLE_KEYS.twitterTitle,
            content: title,
        };
        const googleTitle = {
            itemprop: NGX_SEO_TITLE_KEYS.name,
            content: title,
        };
        this.meta.removeTag('itemprop="name"'); // Because if we not remove the tag it will not be updated.
        this.title.setTitle(title);
        this.meta.updateTag(ogTitle);
        this.meta.updateTag(twitterTitle);
        this.meta.updateTag(googleTitle);
    }
    setMeta(meta) {
        if (meta.keywords) {
            this.setMetaKeywords(meta.keywords);
        }
        if (meta.description) {
            this.setMetaDescription(meta.description);
        }
        if (meta.type) {
            this.setMetaType(meta.type);
        }
        if (meta.card) {
            this.setMetaCard(meta.card);
        }
        if (meta.image) {
            this.setMetaImage(meta.image);
        }
        if (meta.url) {
            this.setMetaUrl(meta.url);
        }
        if (meta.author) {
            this.setMetaAuthor(meta.author);
        }
        if (meta.siteName) {
            this.setMetaSiteName(meta.siteName);
        }
        if (meta.canonical) {
            this.setMetaCanonical(meta.canonical);
        }
        if (meta.customTags && meta.customTags.length > 0) {
            this.setMetaCustomTags(meta.customTags);
        }
    }
    setMetaKeywords(metaKeywords) {
        const keywordsContent = typeof metaKeywords === 'string' ? metaKeywords : metaKeywords.join(', ');
        const keywords = {
            name: NGX_SEO_META_KEYS.keywords,
            content: keywordsContent,
        };
        this.meta.updateTag(keywords);
    }
    setMetaDescription(metaDescription) {
        const description = {
            name: NGX_SEO_META_KEYS.description,
            content: metaDescription,
        };
        const ogDescription = {
            property: NGX_SEO_META_KEYS.ogDescription,
            content: metaDescription,
        };
        const twitterDescription = {
            name: NGX_SEO_META_KEYS.twitterDescription,
            content: metaDescription,
        };
        const googleDescription = {
            itemprop: NGX_SEO_META_KEYS.description,
            content: metaDescription,
        };
        this.meta.removeTag('itemprop="description"'); // Because if we not remove the tag it will not be updated.
        this.meta.updateTag(description);
        this.meta.updateTag(ogDescription);
        this.meta.updateTag(twitterDescription);
        this.meta.updateTag(googleDescription);
    }
    setMetaType(metaType) {
        const ogType = {
            property: NGX_SEO_META_KEYS.ogType,
            content: metaType,
        };
        this.meta.updateTag(ogType);
    }
    setMetaCard(metaCard) {
        const twitterCard = {
            name: NGX_SEO_META_KEYS.twitterCard,
            content: metaCard,
        };
        this.meta.updateTag(twitterCard);
    }
    setMetaImage(metaImage) {
        const ogImage = {
            property: NGX_SEO_META_KEYS.ogImage,
            content: metaImage,
        };
        const twitterImage = {
            name: NGX_SEO_META_KEYS.twitterImage,
            content: metaImage,
        };
        const googleImage = {
            itemprop: NGX_SEO_META_KEYS.image,
            content: metaImage,
        };
        this.meta.removeTag('itemprop="image"'); // Because if we not remove the tag it will not be updated.
        this.meta.updateTag(ogImage);
        this.meta.updateTag(twitterImage);
        this.meta.updateTag(googleImage);
    }
    setMetaUrl(metaUrl) {
        const ogUrl = {
            property: NGX_SEO_META_KEYS.ogURL,
            content: metaUrl,
        };
        const twitterUrl = {
            name: NGX_SEO_META_KEYS.twitterURL,
            content: metaUrl,
        };
        this.meta.updateTag(ogUrl);
        this.meta.updateTag(twitterUrl);
    }
    setMetaAuthor(metaAuthor) {
        const author = {
            name: NGX_SEO_META_KEYS.author,
            content: metaAuthor,
        };
        this.meta.updateTag(author);
    }
    setMetaSiteName(metaSiteName) {
        const siteName = {
            name: NGX_SEO_META_KEYS.ogSiteName,
            content: metaSiteName,
        };
        this.meta.updateTag(siteName);
    }
    setMetaCanonical(metaCanonical) {
        const canonical = {
            name: NGX_SEO_META_KEYS.canonical,
            content: metaCanonical,
        };
        this.meta.updateTag(canonical);
    }
    setMetaCustomTags(customTags) {
        customTags.forEach((customTag) => {
            this.meta.updateTag(customTag);
        });
    }
    /**
     * Will remove all meta tags from HTML document.
     */
    removeMeta() {
        Object.values(NGX_SEO_META_KEYS).forEach((value) => {
            this.meta.removeTag(`name="${value}"`);
            this.meta.removeTag(`itemprop="${value}"`);
            this.meta.removeTag(`property="${value}"`);
        });
    }
    /**
     * Will listen to router changes and if seo key exist in router data try to add it to HTML document tags.
     */
    subscribe() {
        return this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), map(() => this.activatedRoute), map((route) => {
            while (route.firstChild) {
                route = route.firstChild;
            }
            return route;
        }), filter((route) => route.outlet === 'primary'), mergeMap((route) => route.data))
            .subscribe((data) => {
            if (data['seo']) {
                this.setSeo(data['seo']);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoService, deps: [{ token: NGX_SEO_CONFIG_TOKEN }, { token: i1.ActivatedRoute }, { token: i2.Meta }, { token: i1.Router }, { token: i2.Title }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGX_SEO_CONFIG_TOKEN]
                }] }, { type: i1.ActivatedRoute }, { type: i2.Meta }, { type: i1.Router }, { type: i2.Title }]; } });

class NgxSeoModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('NgxSeoModule is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(config = {}) {
        const updatedConfig = {
            ...defaultNgxSeoConfig,
            ...config,
        };
        return {
            ngModule: NgxSeoModule,
            providers: [
                NgxSeoService,
                {
                    provide: NGX_SEO_CONFIG_TOKEN,
                    useValue: updatedConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: () => () => {
                        return;
                    },
                    deps: [NgxSeoService],
                    multi: true,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoModule, deps: [{ token: NgxSeoModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: NgxSeoModule, decorators: [{
            type: NgModule
        }], ctorParameters: function () { return [{ type: NgxSeoModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { NGX_SEO_CONFIG_TOKEN, NgxSeoModule, NgxSeoService, defaultNgxSeoConfig };
